import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { fetchExperienceProjects, updateExperience, deleteExperienceProjects } from '../../../api/experience';
import { COLORS } from '../../../constants/colors';
import useFetch from '../../../hooks/useFetch';
import DatePicker from '../../common/datePicker';
import Input from '../../common/input';
import Project from '../../project';
import useNotification from '../../../hooks/useNotification';
import { faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouteMatch } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import Searcher from '../../../components/role/searcher';

const useStyles = makeStyles({
  root: {
    '&:first-of-type': {
      marginTop: '1.3125rem',
    },
    '&:not(:last-child)': {
      marginBottom: '2.5rem',
    },
  },
  border: {
    height: '3.9375rem',
    width: '.75rem',
    marginRight: '1.25rem',
    backgroundColor: COLORS.primary.main,
    borderRadius: '.3125rem',
  },
  company: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    lineHeight: '150%',
  },
  detail: {
    fontSize: '1.25rem',
    lineHeight: '150%',
  },
  position: {
    marginTop: '1.375rem',
    marginLeft: '2rem',
  },
  role: {
    fontSize: '1.5rem',
    lineHeight: '150%',
    marginBottom: '0.625rem',
  },
});

const Edit = ({
   hash,
   company: initialCompany,
   since: initialSince,
   until: initialUntil,
   onDelete,
   reloadCandidateUpdatedAtData
   }) => {
  const { control, errors, setValue } = useForm({});
  const classes = useStyles();
  const notifications = useNotification();
  const [changes, setChanges ] = useState(0);
  const [experienceHash, setExperienceHash] = useState('');

  useEffect(() => {
    if(hash && !experienceHash) {
      setExperienceHash(hash);
    }
  }, [experienceHash, hash])
  
  const isEnabled = useRouteMatch({
    path: '/candidates/:hash/edit',
    exact: true,
  });
  const { result: initialProjectsByPosition, isLoading } = useFetch(
    fetchExperienceProjects,
    hash,
    [hash, changes]
  );

  const [values, setValues] = useState({company: initialCompany, since: initialSince, until: (!isNaN(new Date(initialUntil).getTime())) ? initialUntil : null});
  const [lastValues, setLastValues] = useState({company: initialCompany, since: initialSince, until: (!isNaN(new Date(initialUntil).getTime())) ? initialUntil : null});
  const [projectsByPosition, setProjectsByPosition ] = useState(initialProjectsByPosition);

  useEffect(() => {
    if (!isLoading) {
      setProjectsByPosition(initialProjectsByPosition);
      setChanges(0);
    }
  }, [ isLoading, initialProjectsByPosition ])

  useEffect(() => {
    if(values.since && values.company && (!values.until || (values.until && new Date(values.until) > new Date(values.since))) )
      handleSubmit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.since, values.until])

  const updateProjectByPositionHashChanged = (index, value) => {
    if (value && value.name && value.hash){
      let newArr = [...projectsByPosition]; 
      newArr[index].role = value.name; 
      newArr[index].hash = value.hash;
      setProjectsByPosition(newArr);
    }
  }

  const handleSubmit = () => {
    if (lastValues.company!== values.company || 
        lastValues.since  !== values.since || 
        lastValues.until  !== values.until ) {
          updateExperience(hash, { immediately: true, company:values.company, since:values.since, until:values.until?values.until:"Current" })
          .then((res) => {
            if (res?.success) {
              setLastValues({company: values.company, since: values.since, until: values.until})
              reloadCandidateUpdatedAtData()
              notifications.success({ message: 'Experience was successfully updated', });
            } else notifications.error(res.error);
          });
    }
  }

  function handleDeleteExperience() {
    onDelete(hash);
  }

  const appendProject = index => {
    const currentPositions = [...projectsByPosition];
    currentPositions[index].projects.push({
      'description': '',
      'name': '',
      'skills': []
    });
    setProjectsByPosition(currentPositions);
  };

  function handleDeleteProject(project) {
    deleteExperienceProjects(hash, { immediately: true, project }).then((res) => {
      if (res?.success) {
        setChanges((prev) => prev + 1 );
        reloadCandidateUpdatedAtData()
        notifications.success({
          message: 'Project was successfully deleted',
        });
      } else notifications.error(res.error);
    });
  }

  return (
    <Grid className={classes.root} container direction="row" disabled={!isEnabled}>
      <Grid className={classes.border} item />
      <Grid item container xs justify="space-between">
        <Grid item xs={3}>
          <Input
            placeholder="Company"
            className={classes.company}
            variant="h1"
            rows={6}
            value={values.company}
            onChange={({ target }) => setValues({...values, company:target.value}) }
            onBlur={() => {
              if(values.since && values.company 
                && ( !values.until ||  (values.until && new Date(values.until) > new Date(values.since)) )
              ) handleSubmit()
            }}
            invalid={values.company?false:true}
            message={!values.company && "Company is required"}
          />
        </Grid>

        <Grid item xs={3}>
          <DatePicker
            label="Since"
            id={"DatePicker-experience-since-edit_"+hash}
            date={values.since}
            openTo="year"
            views={["year", "month"]}
            autoOk={true}
            maxDate={new Date()}
            maxDateMessage={'Date should not be after today'}
            onChange={(date) =>setValues({...values, since: date?`${date.toLocaleString('en-US', { month: 'long' })} ${date.getFullYear()}`:null})}
            invalid={(!values.since || (values.until && values.since && new Date(values.until) < new Date(values.since)))}
            message={(!values.since && "Since date is required")
              || (values.until && new Date(values.until) < new Date(values.since)  && "Since must be smaller than Until")
            }
          />
        </Grid>

        <Grid item xs={3}>
          <DatePicker
            label="Until"
            id={"DatePicker-experience-until-edit_"+hash}
            date={values.until}
            openTo="year"
            views={["year", "month"]}
            autoOk={true}
            maxDate={new Date()}
            maxDateMessage={'Date should not be after today'}
            onChange={(date) =>setValues({...values, until: date?`${date.toLocaleString('en-US', { month: 'long' })} ${date.getFullYear()}`:null}) }
            invalid={(values.until && values.since && new Date(values.until) < new Date(values.since))}
            message={(!values.until && "Until date is 'Current / Present'")
              || (values.until && new Date(values.until) < new Date(values.since)  && "Until must be grather than Since")
            }
          />
        </Grid>

        <Grid item>
          <Button variant="outlined" color="primary" onClick={() => handleDeleteExperience()}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Grid>

      </Grid>
      {Array.isArray(projectsByPosition) &&
        projectsByPosition.map(({ role, hash, projects }, index) => (
          <Grid
            className={classes.position}
            key={`position-${index}`}
            container
          >
            <Grid item xs={12}>
              <Typography className={classes.role} variant="button" component="h4">
                Position
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="role"
                  defaultValue={{hash: hash, name: role}}
                  render={({ onChange, onBlur, value }) => (
                    <div className={classes.searcher}>
                      <Searcher 
                        value={value}
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        invalid={errors && errors.role}
                        message={errors && errors.role && errors.role.message}
                        onSelect={(val)=>{
                          updateProjectByPositionHashChanged(index, val);
                        }} 
                      />
                    </div>
                  )}
                />
            </Grid>
            <Grid item container direction="column">
              {projects.map((project, projectIdx) => (
                <Project 
                  edit={true} 
                  key={project.hash || `project-${projectIdx}`} 
                  onDelete={() => handleDeleteProject(project.hash)} onSubmit={() => setChanges((prev) => prev + 1)}
                  role={role} 
                  roleHash={hash}
                  appendProject={() => appendProject(index)}
                  projectIdx={index}
                  experienceHash={experienceHash}
                  reloadCandidateUpdatedAtData={reloadCandidateUpdatedAtData}
                  {...project} 
                />
              ))}
            </Grid>
            <Grid>
        </Grid>
          </Grid>
   ))}
    </Grid>
  );
};

Edit.defaultProps = {
  overview: '',
};

Edit.propTypes = {
  overview: PropTypes.string,
};

export default Edit;
